import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spin } from 'antd';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  Subtitle,
  StatusBadge,
  BadgeContainer,
  LinkButton,
  TextContainer,
  NotificationContentContainer,
  NotificationContainer,
  AlarmTypeBadge,
  NotificationGlobalStyles,
} from './styles';

export type AlarmData = {
  disparo: string;
  idAlarme: string;
  nomeAlarme: string;
  quantidadeDisparo: string;
  tipoAlarme: string;
  tipoAlarmeOriginal: string | null;
  tipoDisparo: 'critical' | 'ok' | 'crit' | 'recovery';
  valorDisparo: string;
  nomeGrafico: string;
  timeId: string;
};

interface AlarmNotificationContentProps {
  navigateToAlarmPage: (alarmData: AlarmData) => void;
  alarmData?: AlarmData;
  isToast?: boolean;
  // Props para o botão de remover
  onRemove?: (idAlarme: string, timeId: string) => void;
  isRemoving?: boolean; // Para controlar o estado de loading
  showRemoveButton?: boolean; // Para controlar se deve mostrar o botão
}

export function AlarmNotificationContent({
  navigateToAlarmPage,
  alarmData,
  isToast = false,
  onRemove,
  isRemoving = false,
  showRemoveButton = true,
}: AlarmNotificationContentProps) {
  const { t } = useTranslation(['alarms-notifications', 'alerts']);

  if (!alarmData) {
    return null;
  }

  return (
    <>
      {/* @ts-ignore */}
      <NotificationGlobalStyles isToast={isToast} alarmData={!!alarmData} />
      <NotificationContainer isToast={isToast}>
        <NotificationContentContainer>
          {showRemoveButton && onRemove && (
            <Button
              size="small"
              icon={
                isRemoving ? (
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 16 }} spin />
                    }
                  />
                ) : (
                  <CloseOutlined />
                )
              }
              loading={isRemoving}
              disabled={isRemoving}
              onClick={e => {
                e.stopPropagation();
                if (!isRemoving) {
                  onRemove?.(alarmData.idAlarme, alarmData.timeId);
                }
              }}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 10,
                border: 'none',
              }}
              title={t('removeNotification')}
            />
          )}
          <BadgeContainer>
            <StatusBadge variant={alarmData.tipoDisparo}>
              {(() => {
                if (alarmData.tipoDisparo === 'critical')
                  return t('criticalAlarm');
                if (alarmData.tipoDisparo === 'ok') return t('okAlarm');
                if (alarmData.tipoDisparo === 'crit') return t('critAlarm');
                return t('recoveryAlarm');
              })()}
            </StatusBadge>
            <AlarmTypeBadge>
              {(() => {
                const displayType =
                  alarmData.tipoAlarmeOriginal || alarmData.tipoAlarme;
                return t(`alerts:alarm-types.${displayType}`, displayType);
              })()}
            </AlarmTypeBadge>
          </BadgeContainer>
          <TextContainer>
            <Subtitle>{alarmData.nomeAlarme}</Subtitle>
            <LinkButton
              type="link"
              onClick={() => navigateToAlarmPage(alarmData)}
            >
              {t('viewAlarm')}
            </LinkButton>
          </TextContainer>
        </NotificationContentContainer>
      </NotificationContainer>
    </>
  );
}
